import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Breadcrumbs, Button, FormWrapper, ImageUploadModal, ModalWrapper, Table, ViewMediaModal } from "../components";
import { FileTypes } from "../constants";
import useApiService from "../hooks/useApiService";
import { parseAmount } from "../services/utils";

function CanceledPlotRequestsPage() {
  const tableColumns = [
    { id: "plot_number", name: "Plot No." },
    { id: "company_name", name: "Company Name" },
    { id: "project_name", name: "Project Name" },
    { id: "agent_name", name: "Agent Name" },
    { id: "customer_name", name: "Customer Name" },
    { id: "phase_number", name: "Phase Number" },
    { id: "cancelation_status", name: "Status" },
    { id: "action", name: "More Details" },
  ];

  const [canceledPlots, setCanceledPlots] = useState([]);
  const { get, put } = useApiService();
  const [searchParams] = useSearchParams();
  const [selectedCanceledPlotId, setSelectedCanceledPlotId] = useState(null);
  const [canceledPlotData, setCanceledPlotData] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [amountRefundable, setAmountRefundable] = useState("");
  const [showRejectionReasonModal, setShowRejectionReasonModal] = useState(false);
  const [rejectionReasonInputError, setRejectionReasonInputError] = useState("");

  useEffect(() => {
    const id = searchParams.get("id");
    if (id && !isNaN(id)) setSelectedCanceledPlotId(Number(id));
  }, []);

  const fetchCanceledPlots = async () => {
    try {
      const response = await get("/api/bookings/canceled/plots");
      if (response.status && response.data) {
        setCanceledPlots(
          response.data.map((plot) => ({
            ...plot,
            cancelation_status:
              `${plot.cancelation_status}`.charAt(0).toUpperCase() + `${plot.cancelation_status}`.slice(1),
          }))
        );
        return;
      }
      throw new Error("Unable to fetch canceled plots");
    } catch (error) {
      console.error("No canceled plots data available");
    }
  };
  useEffect(() => {
    fetchCanceledPlots();
  }, []);

  const fetchCanceledPlot = async (id) => {
    try {
      const response = await get(`/api/bookings/canceled/${id ?? selectedCanceledPlotId}`);
      const amountPaid = response.data.amount_paid && `${response.data.amount_paid}`.replaceAll(",", "");
      // if there are decimals, restrict it to 2
      const amountToRefund = `${(amountPaid * 0.7).toFixed(2)}`.replace(".00", "");
      const sellingPrice = response.data.total_plot_amount?.replaceAll(",", "") ?? 0;
      const waiveOff = response.data.waive_off_amount?.replaceAll(",", "") ?? 0;
      const soldAt = +sellingPrice - +waiveOff;
      if (response.status && response.data) {
        setCanceledPlotData({
          ...response.data,
          amount_paid: amountPaid,
          plot_area: response.data.plot_area_sq_ft.replace(".00", ""),
          selling_price: +sellingPrice,
          waive_off_amount: +waiveOff,
          sold_at: +soldAt,
        });
        setAmountRefundable(amountToRefund);
        return;
      }
      throw new Error("Unable to fetch plot details");
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    if (selectedCanceledPlotId) fetchCanceledPlot();
  }, [selectedCanceledPlotId]);

  const handleApproveCancellationRequest = async () => {
    if (amountRefundable < 0 || amountRefundable === null) {
      toast.error("Please enter a valid amount to be refunded");
      return;
    }
    if (amountRefundable > canceledPlotData.amount_paid * 0.7) {
      toast.error("Amount to be refunded should be less than 70% of the amount paid");
    }
    try {
      const response = await put(`/api/bookings/canceled/${selectedCanceledPlotId}/approve`, {
        amount_refundable: amountRefundable,
      });
      if (response.status) {
        toast.success("Cancellation request approved successfully");
        await fetchCanceledPlots();
        await fetchCanceledPlot();
        return;
      }
      throw new Error("Unable to approve cancellation request");
    } catch (error) {
      console.error(error);
    }
  };

  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showViewMediaModal, setShowViewMediaModal] = useState(false);
  const handleIdentificationUpload = () => {
    // to do
  };

  const handleSaveRejectionReason = async () => {
    if (!rejectionReason || rejectionReason.trim() === "") {
      setRejectionReasonInputError("Please enter a valid reason for rejection");
      return;
    }
    setShowRejectionReasonModal(false);
    try {
      const response = await put(`/api/bookings/canceled/${selectedCanceledPlotId}/reject`, {
        rejection_reason: rejectionReason,
      });
      if (response.status) {
        toast.success("Cancellation request rejected successfully");
        await fetchCanceledPlots();
        await fetchCanceledPlot();
        return;
      }
      throw new Error("Unable to reject cancellation request");
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!showRejectionReasonModal) {
      setRejectionReason("");
      setRejectionReasonInputError("");
    }
  }, [showRejectionReasonModal]);

  useEffect(() => {
    if (rejectionReason.length > 0) setRejectionReasonInputError("");
  }, [rejectionReason]);

  const detailsRef = useRef();
  useEffect(() => {
    if (selectedCanceledPlotId && detailsRef.current)
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedCanceledPlotId]);

  const renderDataRow = (label, value) => {
    return (
      <div className="row">
        <span className="label">{label}</span>
        <span className="value">{value}</span>
      </div>
    );
  };

  return (
    <>
      <Breadcrumbs />
      <Table
        columns={tableColumns}
        data={canceledPlots}
        title="Canceled Plot Requests"
        setSelectedId={(id) => {
          setSelectedCanceledPlotId(id);
          fetchCanceledPlot(id);
        }}
      />

      {selectedCanceledPlotId && canceledPlotData && (
        <section className="section_wrapper" ref={detailsRef}>
          <section className="section_title_wrapper">
            <h2 className="section_title">Canceled Plot Request</h2>
            <div className={`status status_${canceledPlotData.cancelation_status?.toLowerCase()}`}>
              {canceledPlotData.cancelation_status === "approved"
                ? "Accepted"
                : `${canceledPlotData.cancelation_status}`.charAt(0).toUpperCase() +
                  canceledPlotData.cancelation_status.slice(1)}
            </div>
          </section>
          {canceledPlotData.cancelation_status?.toLowerCase() === "verifying" && (
            <FormWrapper>
              <section className="section grid_3">
                <div className="column">
                  {renderDataRow("Company Name", canceledPlotData.company_name)}
                  {renderDataRow("Project Name", canceledPlotData.project_name)}
                  {renderDataRow("Plot Number", canceledPlotData.plot_number)}
                  {renderDataRow("Phase Number", canceledPlotData.phase_number)}
                  {renderDataRow("Plot Area", `${canceledPlotData.plot_area} sq.ft`)}
                  {renderDataRow("Rate/sq.ft", parseAmount(canceledPlotData.rate_per_sq_ft))}
                  {renderDataRow("Amount Paid", parseAmount(canceledPlotData.amount_paid))}
                  {renderDataRow("Amount Receivable", parseAmount(canceledPlotData.amount_receivable))}
                  <div className="row">
                    <span className="label">Amount Refundable</span>
                    <div className="input_with_prefix_wrapper">
                      <span className="prefix">₹</span>
                      <input
                        type="number"
                        value={amountRefundable}
                        onChange={(e) => setAmountRefundable(e.target.value)}
                        placeholder="Enter amount"
                        className="input"
                        max={canceledPlotData.amount_paid * 0.7}
                        required
                        min={0}
                      />
                    </div>
                  </div>
                </div>
                <div className="vertical_divider"></div>
                <div className="column">
                  {renderDataRow("Selling Price", parseAmount(canceledPlotData.selling_price))}
                  {renderDataRow("Sold at", parseAmount(canceledPlotData.sold_at))}
                  {renderDataRow("Waive Off Amount", parseAmount(canceledPlotData.waive_off_amount))}
                  {renderDataRow("Customer name (as per bank)", canceledPlotData.customer_name)}
                  {renderDataRow("Bank Name", canceledPlotData.bank_name)}
                  {renderDataRow("Type of Account", canceledPlotData.bank_account_type)}
                  {renderDataRow("IFSC Code", canceledPlotData.bank_ifsc)}
                  {renderDataRow("Bank Branch", canceledPlotData.bank_branch)}
                  <div className="row">
                    <span className="label">Canceled Cheque / Passbook</span>
                    {canceledPlotData.bank_identification.url ? (
                      <Button className="ghost" onClick={() => setShowViewMediaModal(true)}>
                        preview
                      </Button>
                    ) : (
                      "No image uploaded"
                    )}
                  </div>
                  {renderDataRow("Reason for cancellation", canceledPlotData.cancellation_reason)}
                </div>
              </section>
              <div className="row end">
                <Button
                  className="approve"
                  onClick={handleApproveCancellationRequest}
                  // disabled={!amountRefundable || amountRefundable <= 0}
                >
                  Approve
                </Button>
                <Button className="reject" onClick={() => setShowRejectionReasonModal(true)}>
                  Reject
                </Button>
              </div>
            </FormWrapper>
          )}

          {canceledPlotData.cancelation_status === "approved" && (
            <div className="column">
              <div className="chip color_swatch--green end">Refunded</div>
              <div className="section grid_3">
                <div className="column">
                  {renderDataRow("Project Name", canceledPlotData.project_name)}
                  {renderDataRow("Phase Number", canceledPlotData.phase_number)}
                  {renderDataRow("Plot Number", `#${canceledPlotData.plot_number}`)}
                  {renderDataRow("Customer name", canceledPlotData.customer_name)}
                  {renderDataRow("Amount refunded", parseAmount(canceledPlotData.amount_refundable))}
                </div>
                <div className="vertical_divider"></div>
                <div className="column">
                  {renderDataRow("Selling Price", parseAmount(canceledPlotData.selling_price))}
                  {renderDataRow("Sold at", parseAmount(canceledPlotData.sold_at))}
                  {renderDataRow("Waive off amount", parseAmount(canceledPlotData.waive_off_amount))}
                  {renderDataRow("Total paid amount", parseAmount(canceledPlotData.amount_paid))}
                  {renderDataRow(
                    "Total commission paid before cancellation",
                    parseAmount(canceledPlotData.total_commission_paid_before_cancellation)
                  )}
                </div>
              </div>
            </div>
          )}

          {canceledPlotData.cancelation_status === "rejected" && (
            <div className="section grid_3">
              <div className="column">
                {renderDataRow("Project Name", canceledPlotData.project_name)}
                {renderDataRow("Phase Number", canceledPlotData.phase_number)}
                {renderDataRow("Plot Number", `#${canceledPlotData.plot_number}`)}
                {renderDataRow("Customer name", canceledPlotData.customer_name)}
                {renderDataRow("Total paid amount", parseAmount(canceledPlotData.amount_paid))}
              </div>
              <div className="vertical_divider"></div>
              <div className="column">
                {renderDataRow("Selling Price", parseAmount(canceledPlotData.selling_price))}
                {renderDataRow("Sold at", parseAmount(canceledPlotData.sold_at))}
                {renderDataRow("Waive off amount", parseAmount(canceledPlotData.waive_off_amount))}
                {renderDataRow("Reason for rejection", canceledPlotData.rejection_reason)}
              </div>
            </div>
          )}
        </section>
      )}

      {showImageUploadModal && (
        <ImageUploadModal
          title="Upload Bank Identification"
          isVisible={showImageUploadModal}
          fileType={FileTypes.BANK_IDENTIFICATION}
          handleClose={() => setShowImageUploadModal(false)}
          handleUpload={handleIdentificationUpload}
        />
      )}

      {showViewMediaModal &&
        createPortal(
          <ViewMediaModal
            title="Bank Identification"
            isVisible={showViewMediaModal}
            handleClose={() => setShowViewMediaModal(false)}
            url={[canceledPlotData.bank_identification.url]}
          />,
          document.body
        )}

      {showRejectionReasonModal && (
        <ModalWrapper
          name="rejection_reason_modal"
          isVisible={showRejectionReasonModal}
          onClose={() => setShowRejectionReasonModal(false)}
          level={1}
        >
          <FormWrapper title="Canceled Plot Request" titleCenter>
            <div className="column">
              <div className="input_wrapper">
                <input
                  type="text"
                  name="rejection_reason"
                  required
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection"
                  className="input full"
                  maxLength={200}
                />
                {rejectionReasonInputError && <span className="small error">{rejectionReasonInputError}</span>}
              </div>

              <div className="row center">
                <Button className="cancel" onClick={() => setShowRejectionReasonModal(false)}>
                  Cancel
                </Button>
                <Button className="reject" onClick={handleSaveRejectionReason}>
                  Reject
                </Button>
              </div>
            </div>
          </FormWrapper>
        </ModalWrapper>
      )}
    </>
  );
}

export default CanceledPlotRequestsPage;
